import { useCallback, useRef, useState } from "react";

type FieldEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export type ValidationIssue = { name: string; label: string; message: string };

/**
 * Captures native constraint-validation messages and renders them inline,
 * instead of relying on browser bubbles that can sit off-screen on phones.
 * Keeps a registry of field elements so a summary can move focus back to an
 * invalid control with the keyboard.
 */
export function useInlineValidation() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const fields = useRef(new Map<string, FieldEl>());
  const labels = useRef(new Map<string, string>());
  const focusedFirst = useRef(false);

  const register = useCallback((name: string, el: FieldEl | null) => {
    if (el) fields.current.set(name, el);
    else fields.current.delete(name);
  }, []);

  const revealField = useCallback((el: FieldEl) => {
    if (typeof window === "undefined") return;
    // Sticky header height, plus breathing room so the label and inline error
    // are visible too, not just the top edge of the control.
    const headerVar = getComputedStyle(document.documentElement).getPropertyValue("--site-hdr");
    const header = Number.parseFloat(headerVar) || 72;
    const gutter = header + 72;

    // The label sits above the control, so scroll to the field's wrapper when
    // there is one; that keeps label + input + message in view together.
    const target = el.closest<HTMLElement>("[data-field-wrapper]") ?? el;
    const top = target.getBoundingClientRect().top + window.scrollY - gutter;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });

    // Mark the field we jumped to so it reads as the active error, not just
    // one of several outlined fields.
    document
      .querySelectorAll<HTMLElement>('[data-error-focus="true"]')
      .forEach((node) => node.removeAttribute("data-error-focus"));
    target.setAttribute("data-error-focus", "true");
  }, []);

  const focusField = useCallback(
    (name: string) => {
      const el = fields.current.get(name);
      if (!el) return;
      el.focus({ preventScroll: true });
      revealField(el);
    },
    [revealField],
  );

  const onInvalid = useCallback(
    (event: React.FormEvent<FieldEl>) => {
      // Suppress the native bubble; we render the message inline instead.
      event.preventDefault();
      const el = event.currentTarget;
      const name = el.name || el.id;
      const message = el.validationMessage;
      const label = labels.current.get(name) ?? name;

      setErrors((prev) => ({ ...prev, [name]: message }));
      setIssues((prev) => {
        const next = prev.filter((issue) => issue.name !== name);
        next.push({ name, label, message });
        return next;
      });

      if (!focusedFirst.current) {
        focusedFirst.current = true;
        el.focus({ preventScroll: true });
        // Wait for the summary and inline messages to render, since they shift
        // the field's position before we measure it.
        requestAnimationFrame(() => requestAnimationFrame(() => revealField(el)));
      }
    },
    [revealField],
  );

  const clearError = useCallback((name: string) => {
    fields.current
      .get(name)
      ?.closest<HTMLElement>("[data-field-wrapper]")
      ?.removeAttribute("data-error-focus");
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setIssues((prev) => (prev.some((i) => i.name === name) ? prev.filter((i) => i.name !== name) : prev));
  }, []);

  const resetErrors = useCallback(() => {
    focusedFirst.current = false;
    if (typeof document !== "undefined") {
      document
        .querySelectorAll<HTMLElement>('[data-error-focus="true"]')
        .forEach((node) => node.removeAttribute("data-error-focus"));
    }
    setErrors({});
    setIssues([]);
  }, []);

  const fieldProps = useCallback(
    (name: string, label?: string) => {
      if (label) labels.current.set(name, label);
      const invalid = Boolean(errors[name]);
      return {
        name,
        ref: (el: FieldEl | null) => register(name, el),
        onInvalid,
        onInput: () => clearError(name),
        "aria-invalid": invalid ? (true as const) : undefined,
        "aria-describedby": invalid ? `${name}-error` : undefined,
      };
    },
    [clearError, errors, onInvalid, register],
  );

  return { errors, issues, fieldProps, focusField, clearError, resetErrors };
}
